import { Clickable, Col, H2, P, PortableText, Row, Spinner, Strong, Table, TypeWrapper, Wrapper } from '@ahmdigital/ui';
import { getTaxYear, isTaxTimeReminderActive } from '@ahmdigital/logic/lib/utils/get-tax-dates';
import { head, map, noop } from 'lodash/fp';
import { useQuery } from '@apollo/client/react';

import { Customer } from '../../../../../selectors/select-customer/types';
import { GET_TAX_RATES } from '../../queries/get-tax-rates/query.gql';
import { Rate } from '../../../../../types/__codegen__/sanity/graphql';
import Calculator from '../calculator';
import CalculatorResults from './components/calculator-results';
import constants from '../../../../../ahm-constants';
import getPrefixSentence from './utils/get-prefix-sentence';
import getScaleTableData from './utils/get-scale-table-data';
import getThresholdsFromTaxRate from './utils/get-thresholds-from-tax-rate';
import MlsSection from '../mls-section';
import styles from './styles.module.sass';
import type {
  CalculatorOnChange,
  CalculatorOnIncomeChange,
  CalculatorOnScaleChange,
  CalculatorOnStateChange,
} from '../calculator';

type CalculatorSectionProps = {
  customer: Customer;
  incomeType?: string;
  mlsAmount?: number;
  onChange: CalculatorOnChange;
  onIncomeChange?: CalculatorOnIncomeChange;
  onScaleChange?: CalculatorOnScaleChange;
  onStateChange?: CalculatorOnStateChange;
};

const CalculatorSection = ({
  mlsAmount = 0,
  incomeType = constants.INCOME_TYPE.SINGLE,
  customer,
  onChange,
  onIncomeChange = noop,
  onScaleChange = noop,
  onStateChange = noop,
}: CalculatorSectionProps) => {
  const currentYear = getTaxYear();
  const showTaxReminder = isTaxTimeReminderActive();
  const { data, loading } = useQuery(GET_TAX_RATES);

  const calculatorProps = {
    customer,
    onChange,
    onIncomeChange,
    onScaleChange,
    onStateChange,
  };

  if (!data) return null;

  const taxRates = data?.allRate;

  const activeTaxRate = head(taxRates) as Rate;

  const thresholds = getThresholdsFromTaxRate(activeTaxRate, incomeType);

  return (
    <Wrapper className={styles.calculatorWrapper}>
      <Row>
        <MlsSection />
        <Col df={8} dfOffset={2} sm={12} smOffset={0} variant="marginBottomSpacer1">
          <TypeWrapper variant="center">
            <H2 variant="marginBottomSpacer4">How much extra tax could you be paying?</H2>
            <P>
              If you earn above the income threshold and don’t have the right level of private hospital cover for tax
              purposes, the Medicare Levy Surcharge could add between 1% and 1.5% of your income to your annual income
              tax bill. These rates are set by the Government and apply on top of the{' '}
              <Clickable to={constants.URLS.THIRD_PARTY.ATO_MEDICARE_LEVY_SURCHARGE_EXPLANATION}>
                standard Medicare Levy.
              </Clickable>
            </P>
            <P>
              Even if you earn above the threshold and don’t have hospital cover for a portion of the financial year,
              you’d still have to pay the MLS for that time that you didn’t hold hospital cover.
            </P>
            <P>
              Your MLS rate depends on your income over a full financial year — check the current thresholds in the
              table below and find more at{' '}
              <Clickable to={constants.URLS.THIRD_PARTY.ATO_MEDICARE_LEVY_SURCHARGE_INCOME_THRESHOLDS}>
                ato.gov.au
              </Clickable>
            </P>
          </TypeWrapper>
          {loading && <Spinner />}
          {!loading &&
            map((rate) => {
              const scaleTable = getScaleTableData(rate);
              return (
                <div key={rate.title}>
                  <TypeWrapper variant={['marginTopSpacer4', 'marginBottomSpacer10', 'center']}>
                    <PortableText source={rate.summary} />
                  </TypeWrapper>
                  <TypeWrapper variant={['marginTopSpacer8', 'marginBottomSpacer10']}>
                    <Strong>{rate.title}</Strong>
                  </TypeWrapper>
                  <TypeWrapper variant="center">
                    {/* @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ columns?: undefined; data?: undefined; variant?: undefined; } | { columns: { mls: { colProps: { df: number; }; header: string; key: string; }; length: number; toString(): string; toLocaleString(): string; ... 31 more ...; [Symbol.unscopables](): { ...; }; }; data: FormattedBracketRow[]; variant: string[]; }' is not assignable to type 'IntrinsicAttributes & TableProps'. */}
                    <Table {...{ ...scaleTable }} />
                  </TypeWrapper>
                </div>
              );
            }, taxRates)}
          <TypeWrapper variant="center">
            <P className={styles.paragraphBreak}>
              If you have two or more MLS dependant children, the threshold will increase by $1,500 for each MLS
              dependant child after your first child. Find out more at{' '}
              <Clickable to={constants.URLS.THIRD_PARTY.ATO_MEDICARE_LEVY_SURCHARGE_INCOME_THRESHOLDS}>
                ato.gov.au.
              </Clickable>
            </P>
            <P>
              <Strong>
                Check out our handy Medicare Levy Surcharge calculator below to estimate how MLS could affect you.
              </Strong>
            </P>
            <P>
              Please note the below calculations apply{' '}
              {showTaxReminder
                ? ` up until 30 June ${currentYear}. From 1 July ${currentYear} onwards, the new thresholds listed above will apply`
                : `from 1 July ${currentYear}`}
              . If you’re unsure whether the MLS applies to you, you should speak to a financial advisor.
            </P>
          </TypeWrapper>
        </Col>
      </Row>
      <Row>
        <Col df={10} dfOffset={1} variant="marginBottomSpacer2">
          {!loading && activeTaxRate && <Calculator {...calculatorProps} activeTaxRate={activeTaxRate} />}
        </Col>
      </Row>
      <Row>
        <Col df={8} dfOffset={2} sm={12} smOffset={0} variant="marginBottomSpacer8">
          <CalculatorResults amount={mlsAmount} />
        </Col>
      </Row>
      <Row>
        <Col df={8} dfOffset={2} sm={12} smOffset={0} variant="marginBottomSpacer2">
          <TypeWrapper variant="center">
            {incomeType === constants.INCOME_TYPE.SINGLE ? (
              <P>
                This is a guide only{showTaxReminder && ` and the thresholds are changing on 1 July ${currentYear}`}. It
                is based
                {/* @ts-expect-error - Automatic, Please fix when editing this file */} on{' '}
                {getPrefixSentence(calculatorProps.customer, thresholds)} the income you have selected and assumes you
                have no dependants^. You should speak to a financial advisor who will be able to take into account your
                income and personal situation, including any changes that occur during a tax year.
              </P>
            ) : (
              <P>
                This is a guide only{showTaxReminder && ` and the thresholds are changing on 1 July ${currentYear}`}. It
                is based {/* @ts-expect-error - Automatic, Please fix when editing this file */}
                on {getPrefixSentence(calculatorProps.customer, thresholds)} the family income you have selected and
                assumes you have 0-1 MLS dependent children^. Family income thresholds are increased by $1,500 for each
                MLS dependent child after the first child. You should speak to a financial advisor who will be able to
                take into account your income and personal situation, including any changes that occur during a tax
                year.
              </P>
            )}
            <P>
              ^ ‘Income’ and ‘dependants’ have{' '}
              <Clickable to={constants.URLS.THIRD_PARTY.ATO_MEDICARE_LEVY_SURCHARGE}>
                special meanings for MLS purposes
              </Clickable>
            </P>
          </TypeWrapper>
        </Col>
      </Row>
    </Wrapper>
  );
};

export default CalculatorSection;
